import PageWrapper from "components/PageWrapper";
import SubSidebar from "components/SubSidebar";
import SubSidebarSection from "components/SubSidebar/Section";
import SubSidebarSectionItem from "components/SubSidebar/SectionItem";
import SubSidebarSectionTitle from "components/SubSidebar/SectionTitle";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Box from "ds/components/Box";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { isSaasDistribution, isSelfHostedDistribution } from "utils/distribution";
import { isFedRampRegion } from "utils/region";
import useIsOnlySpaceAdmin from "shared/Space/useIsOnlySpaceAdmin";
import useAacFlags from "hooks/useAacFlags";

import SettingsAccountDeleteWarning from "./AccountDeleteWarning";
import { pathConfig } from "./constants";
import { SettingsContext } from "./Context";
import SettingsRoutes from "./Routes";
import styles from "./styles.module.css";
import {
  OrganizationAccessTabs,
  OrganizationAccountTabs,
  OrganizationAuthTabs,
  OrganizationSecurityTabs,
} from "./types";

const isSaas = isSaasDistribution();
const isSelfHosted = isSelfHostedDistribution();
const isFedRamp = isFedRampRegion();

const Settings = () => {
  const { securityEmail } = useTypedContext(SettingsContext);
  const { billingUsage, runSummarizationAi, oidcSubjectTemplateFrontend } = useTypedFlags();

  const { nonRootAdminRoleBindings } = useAacFlags();
  const isOnlySpaceAdmin = useIsOnlySpaceAdmin();

  if (isOnlySpaceAdmin) {
    return (
      <>
        <ViewHeader noBreadCrumbs>
          <ViewHeaderWrapper direction="row">
            <ViewHeaderTitle>Organization settings</ViewHeaderTitle>
          </ViewHeaderWrapper>
        </ViewHeader>
        <Box className={styles.content}>
          <SubSidebar>
            <SubSidebarSection>
              <SubSidebarSectionTitle>Access control center</SubSidebarSectionTitle>
              <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccessTabs.ROLES]}`}>
                Roles
              </SubSidebarSectionItem>
            </SubSidebarSection>

            {nonRootAdminRoleBindings && (
              <SubSidebarSection>
                <SubSidebarSectionTitle>Identity management</SubSidebarSectionTitle>
                <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccessTabs.USERS]}`}>
                  Users
                </SubSidebarSectionItem>
                <SubSidebarSectionItem
                  to={`../${pathConfig[OrganizationAccessTabs.IDP_GROUP_MAPPING]}`}
                >
                  IdP group mapping
                </SubSidebarSectionItem>
                <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccessTabs.API_KEYS]}`}>
                  API keys
                </SubSidebarSectionItem>
              </SubSidebarSection>
            )}
          </SubSidebar>
          <PageWrapper className={styles.wrapper}>
            <SettingsRoutes />
          </PageWrapper>
        </Box>
      </>
    );
  }

  return (
    <>
      <ViewHeader noBreadCrumbs>
        <ViewHeaderWrapper direction="row">
          <ViewHeaderTitle>Organization settings</ViewHeaderTitle>
        </ViewHeaderWrapper>
      </ViewHeader>
      <SettingsAccountDeleteWarning />
      <Box className={styles.content}>
        <SubSidebar>
          <SubSidebarSection>
            <SubSidebarSectionTitle>Access control center</SubSidebarSectionTitle>
            <SubSidebarSectionItem
              to={`../${pathConfig[OrganizationAccessTabs.MANAGEMENT_STRATEGY]}`}
            >
              Management strategy
            </SubSidebarSectionItem>
            <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccessTabs.SPACES]}`}>
              Spaces
            </SubSidebarSectionItem>

            <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccessTabs.LOGIN_POLICIES]}`}>
              Login policy
            </SubSidebarSectionItem>
            <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccessTabs.ROLES]}`}>
              Roles
            </SubSidebarSectionItem>
          </SubSidebarSection>

          <SubSidebarSection>
            <SubSidebarSectionTitle>Identity management</SubSidebarSectionTitle>
            <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccessTabs.USERS]}`}>
              Users
            </SubSidebarSectionItem>
            <SubSidebarSectionItem
              to={`../${pathConfig[OrganizationAccessTabs.IDP_GROUP_MAPPING]}`}
            >
              IdP group mapping
            </SubSidebarSectionItem>
            <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccessTabs.API_KEYS]}`}>
              API keys
            </SubSidebarSectionItem>
          </SubSidebarSection>
          <SubSidebarSection>
            <SubSidebarSectionTitle>Authentication</SubSidebarSectionTitle>
            <SubSidebarSectionItem to={`../${pathConfig[OrganizationAuthTabs.SSO]}`}>
              Single Sign-on
            </SubSidebarSectionItem>

            <SubSidebarSectionItem to={`../${pathConfig[OrganizationAuthTabs.MFA]}`}>
              Multi-factor authentication
            </SubSidebarSectionItem>
          </SubSidebarSection>

          <SubSidebarSection>
            <SubSidebarSectionTitle>SECURITY</SubSidebarSectionTitle>

            {isSaas && (
              <SubSidebarSectionItem
                to={`../${pathConfig[OrganizationSecurityTabs.SECURITY_EMAIL]}`}
              >
                Security email
              </SubSidebarSectionItem>
            )}
            <SubSidebarSectionItem to={`../${pathConfig[OrganizationSecurityTabs.SESSIONS]}`}>
              Sessions
            </SubSidebarSectionItem>
            <SubSidebarSectionItem
              to={`../${pathConfig[OrganizationSecurityTabs.RUNTIME_SECURITY]}`}
            >
              Runtime Security
            </SubSidebarSectionItem>
            {oidcSubjectTemplateFrontend && (
              <SubSidebarSectionItem
                to={`../${pathConfig[OrganizationSecurityTabs.OIDC_SUBJECT_TEMPLATE]}`}
              >
                OIDC subject template
              </SubSidebarSectionItem>
            )}
          </SubSidebarSection>

          <SubSidebarSection>
            <SubSidebarSectionTitle>ACCOUNT</SubSidebarSectionTitle>

            {isSaas && (
              <>
                {!isFedRamp && (
                  <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccountTabs.BILLING]}`}>
                    Billing
                  </SubSidebarSectionItem>
                )}
                {billingUsage && (
                  <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccountTabs.USAGE]}`}>
                    Usage
                  </SubSidebarSectionItem>
                )}
                <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccountTabs.LIMITS]}`}>
                  Limits
                </SubSidebarSectionItem>
              </>
            )}
            {isSelfHosted && (
              <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccountTabs.USAGE_EXPORT]}`}>
                Usage export
              </SubSidebarSectionItem>
            )}
            {runSummarizationAi && (
              <SubSidebarSectionItem to={`../${pathConfig[OrganizationAccountTabs.AI]}`}>
                Artificial Intelligence
              </SubSidebarSectionItem>
            )}
            {isSaas && (
              <SubSidebarSectionItem
                to={`../${pathConfig[OrganizationAccountTabs.ACCOUNT_DELETION]}`}
              >
                Account deletion
              </SubSidebarSectionItem>
            )}
          </SubSidebarSection>
        </SubSidebar>

        <PageWrapper className={styles.wrapper}>
          <SettingsRoutes securityEmail={securityEmail} />
        </PageWrapper>
      </Box>
    </>
  );
};

export default Settings;
